import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, MessageSquare, Send, Clock, CheckCircle,
  AlertCircle, HelpCircle, FileText, RefreshCw, X, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const TICKET_TYPES = [
  { value: 'BUG', label: 'Bug Report', icon: AlertCircle, color: '#f87171' },
  { value: 'ACCOUNT', label: 'Account Issue', icon: HelpCircle, color: '#60a5fa' },
  { value: 'BILLING', label: 'Billing', icon: FileText, color: '#34d399' },
  { value: 'FEATURE', label: 'Feature Request', icon: Plus, color: '#a78bfa' },
  { value: 'OTHER', label: 'Other', icon: MessageSquare, color: '#94a3b8' }
];

const STATUS_CONFIG = {
  OPEN: { label: 'Open', color: '#60a5fa', bg: 'rgba(96,165,250,0.1)' },
  IN_PROGRESS: { label: 'In Progress', color: '#fbbf24', bg: 'rgba(251,191,36,0.1)' },
  WAITING_ON_USER: { label: 'Waiting on You', color: '#f97316', bg: 'rgba(249,115,22,0.1)' },
  RESOLVED: { label: 'Resolved', color: '#34d399', bg: 'rgba(52,211,153,0.1)' },
  CLOSED: { label: 'Closed', color: '#71717a', bg: 'rgba(113,113,122,0.1)' }
};

const TicketsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  
  // Create form state
  const [formType, setFormType] = useState('BUG');
  const [formSubject, setFormSubject] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPriority, setFormPriority] = useState('MEDIUM');
  const [creating, setCreating] = useState(false);
  
  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);
  
  // Fetch tickets
  const fetchTickets = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);
  
  // Fetch ticket detail
  const fetchTicketDetail = async (ticketId) => {
    try {
      const response = await axios.get(`${API_BASE}/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTicket(response.data);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };
  
  // Create ticket
  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    if (!formSubject.trim() || formSubject.length < 5) {
      toast.error('Subject must be at least 5 characters');
      return;
    }
    if (!formDescription.trim() || formDescription.length < 10) {
      toast.error('Description must be at least 10 characters');
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API_BASE}/tickets`, {
        type: formType,
        subject: formSubject,
        description: formDescription,
        priority: formPriority
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Ticket created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };
  
  // Reply to ticket
  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedTicket) return;
    
    setSending(true);
    try {
      await axios.post(`${API_BASE}/tickets/${selectedTicket.id}/reply`, {
        message: replyText
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Reply sent');
      setReplyText('');
      fetchTicketDetail(selectedTicket.id);
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };
  
  // Close ticket
  const handleCloseTicket = async () => {
    if (!selectedTicket) return;
    
    try {
      await axios.patch(`${API_BASE}/tickets/${selectedTicket.id}/close`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ticket closed');
      setSelectedTicket(null);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to close ticket');
    }
  };
  
  const resetForm = () => {
    setFormType('BUG');
    setFormSubject('');
    setFormDescription('');
    setFormPriority('MEDIUM');
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#09090b' }}>
      {/* Header */}
      <div className="border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/app')}
                className="p-2 rounded-lg transition-colors"
                style={{ color: '#71717a' }}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold" style={{ color: '#f4f4f5' }}>
                  Support Tickets
                </h1>
                <p className="text-sm" style={{ color: '#71717a' }}>
                  {tickets.filter(t => t.status !== 'CLOSED').length} open ticket{tickets.filter(t => t.status !== 'CLOSED').length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchTickets}
                disabled={loading}
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                size="sm"
                onClick={() => setShowCreateModal(true)}
                data-testid="new-ticket-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Ticket
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-12" style={{ color: '#71717a' }}>
            Loading tickets...
          </div>
        ) : tickets.length === 0 ? (
          <div 
            className="text-center py-12 rounded-xl"
            style={{ 
              backgroundColor: 'rgba(255,255,255,0.02)',
              border: '1px dashed rgba(255,255,255,0.1)'
            }}
          >
            <MessageSquare className="w-12 h-12 mx-auto mb-4" style={{ color: '#3f3f46' }} />
            <h3 className="text-lg font-medium mb-2" style={{ color: '#a1a1aa' }}>
              No tickets yet
            </h3>
            <p className="mb-4" style={{ color: '#71717a' }}>
              Need help? Create a support ticket
            </p>
            <Button onClick={() => setShowCreateModal(true)} data-testid="create-first-ticket-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Ticket
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => {
              const typeInfo = TICKET_TYPES.find(t => t.value === ticket.type);
              const statusInfo = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.OPEN;
              const TypeIcon = typeInfo?.icon || MessageSquare;
              
              return (
                <button
                  key={ticket.id}
                  onClick={() => fetchTicketDetail(ticket.id)}
                  className="w-full p-4 rounded-xl flex items-center justify-between text-left transition-colors"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.02)',
                    border: `1px solid ${ticket.hasUnread ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.06)'}`
                  }}
                  data-testid={`ticket-${ticket.ticketNumber}`}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{
                        backgroundColor: `${typeInfo?.color}15`,
                        color: typeInfo?.color
                      }}
                    >
                      <TypeIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium" style={{ color: '#f4f4f5' }}>
                          {ticket.ticketNumber}
                        </span>
                        <span 
                          className="text-xs px-2 py-0.5 rounded"
                          style={{
                            backgroundColor: statusInfo.bg,
                            color: statusInfo.color
                          }}
                        >
                          {statusInfo.label}
                        </span>
                        {ticket.hasUnread && (
                          <span className="w-2 h-2 rounded-full bg-blue-500" />
                        )}
                      </div>
                      <p className="text-sm mt-0.5 truncate max-w-md" style={{ color: '#a1a1aa' }}>
                        {ticket.subject}
                      </p>
                      <p className="text-xs mt-1 flex items-center gap-1" style={{ color: '#52525b' }}>
                        <Clock className="w-3 h-3" />
                        {formatDate(ticket.updatedAt)}
                      </p>
                    </div>
                  </div>
                  
                  <ChevronDown className="w-5 h-5 -rotate-90" style={{ color: '#52525b' }} />
                </button>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Create Ticket Modal */}
      {showCreateModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowCreateModal(false)}
        >
          <div 
            className="w-full max-w-lg rounded-xl p-6"
            style={{
              backgroundColor: '#18181b',
              border: '1px solid rgba(255,255,255,0.08)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold" style={{ color: '#f4f4f5' }}>
                Create Support Ticket
              </h2>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{ color: '#71717a' }}
                data-testid="close-modal-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleCreateTicket} className="space-y-4">
              {/* Type */}
              <div>
                <label className="block text-sm mb-1.5" style={{ color: '#a1a1aa' }}>Type</label>
                <select
                  value={formType}
                  onChange={(e) => setFormType(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm appearance-none cursor-pointer"
                  style={{
                    backgroundColor: '#27272a',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#f4f4f5'
                  }}
                  data-testid="ticket-type-select"
                >
                  {TICKET_TYPES.map((type) => (
                    <option key={type.value} value={type.value} style={{ backgroundColor: '#27272a', color: '#f4f4f5' }}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Subject */}
              <div>
                <label className="block text-sm mb-1.5" style={{ color: '#a1a1aa' }}>Subject</label>
                <input
                  type="text"
                  value={formSubject}
                  onChange={(e) => setFormSubject(e.target.value)}
                  placeholder="Brief summary of your issue"
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: '#27272a',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#f4f4f5'
                  }}
                  maxLength={200}
                  required
                  data-testid="ticket-subject-input"
                />
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm mb-1.5" style={{ color: '#a1a1aa' }}>Description</label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Please describe your issue in detail..."
                  rows={5}
                  className="w-full px-3 py-2 rounded-lg text-sm resize-none"
                  style={{
                    backgroundColor: '#27272a',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#f4f4f5'
                  }}
                  maxLength={5000}
                  required
                  data-testid="ticket-description-input"
                />
              </div>
              
              {/* Priority */}
              <div>
                <label className="block text-sm mb-1.5" style={{ color: '#a1a1aa' }}>Priority</label>
                <div className="flex gap-2">
                  {['LOW', 'MEDIUM', 'HIGH'].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setFormPriority(p)}
                      className="flex-1 px-3 py-2 rounded-lg text-sm transition-colors"
                      style={{
                        backgroundColor: formPriority === p ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${formPriority === p ? 'rgba(59,130,246,0.5)' : 'rgba(255,255,255,0.08)'}`,
                        color: formPriority === p ? '#60a5fa' : '#a1a1aa'
                      }}
                      data-testid={`priority-${p.toLowerCase()}`}
                    >
                      {p.charAt(0) + p.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" disabled={creating} data-testid="submit-ticket-btn">
                  {creating ? 'Creating...' : 'Create Ticket'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' }}
          onClick={() => setSelectedTicket(null)}
        >
          <div 
            className="w-full max-w-2xl max-h-[80vh] rounded-xl flex flex-col"
            style={{
              backgroundColor: '#18181b',
              border: '1px solid rgba(255,255,255,0.08)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold" style={{ color: '#f4f4f5' }}>
                    {selectedTicket.ticketNumber}
                  </span>
                  <span 
                    className="text-xs px-2 py-0.5 rounded"
                    style={{
                      backgroundColor: STATUS_CONFIG[selectedTicket.status]?.bg,
                      color: STATUS_CONFIG[selectedTicket.status]?.color
                    }}
                  >
                    {STATUS_CONFIG[selectedTicket.status]?.label}
                  </span>
                </div>
                <p className="text-sm mt-1" style={{ color: '#a1a1aa' }}>
                  {selectedTicket.subject}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {selectedTicket.status !== 'CLOSED' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCloseTicket}
                    data-testid="close-ticket-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Close Ticket
                  </Button>
                )}
                <button
                  onClick={() => setSelectedTicket(null)}
                  style={{ color: '#71717a' }}
                  data-testid="close-detail-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ maxHeight: '400px' }}>
              {selectedTicket.messages?.map((msg, idx) => (
                <div 
                  key={idx}
                  className={`flex ${msg.sender === 'ADMIN' ? 'justify-start' : 'justify-end'}`}
                >
                  <div 
                    className="max-w-[80%] p-3 rounded-xl"
                    style={{
                      backgroundColor: msg.sender === 'ADMIN' 
                        ? 'rgba(59,130,246,0.1)' 
                        : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${msg.sender === 'ADMIN' ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.08)'}`
                    }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium" style={{ 
                        color: msg.sender === 'ADMIN' ? '#60a5fa' : '#a1a1aa' 
                      }}>
                        {msg.sender === 'ADMIN' ? 'Support' : 'You'}
                      </span>
                      <span className="text-xs" style={{ color: '#52525b' }}>
                        {formatDate(msg.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap" style={{ color: '#f4f4f5' }}>
                      {msg.message}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Reply Form */}
            {selectedTicket.status !== 'CLOSED' && (
              <form onSubmit={handleReply} className="p-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    className="flex-1 px-3 py-2 rounded-lg text-sm"
                    style={{
                      backgroundColor: '#27272a',
                      border: '1px solid rgba(255,255,255,0.08)',
                      color: '#f4f4f5'
                    }}
                    data-testid="reply-input"
                  />
                  <Button type="submit" disabled={sending || !replyText.trim()} data-testid="send-reply-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketsPage;
